/**
 * テストストリップ計算機（シート）
 *
 * 印画紙の段階露光時間を計算する。
 *   累積露光 = 基準 × 2^(i × 刻み)、追加 = 累積の差分、合計 = 最後の累積
 * 入力：基準時間（± 0.5 秒、長押しで連続）、本数 3 / 5 / 7、刻み 1 / ½ / ⅓ 段
 * 最後の入力を記憶する。
 */

import { openSheet, holdRepeat, buzz } from './ui.js';

const STORAGE_KEY = 'zn.teststrip.last';

function loadLast() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
}
function saveLast(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function calculate(base, strips, stepStops) {
  const cumulative = [];
  for (let i = 0; i < strips; i++) cumulative.push(base * Math.pow(2, i * stepStops));
  const deltas = [];
  for (let i = 1; i < cumulative.length; i++) deltas.push(cumulative[i] - cumulative[i - 1]);
  return { cumulative, deltas, totalSec: cumulative[cumulative.length - 1] };
}

function fmtSec(s) {
  return `${(Math.round(s * 10) / 10).toFixed(1)}s`;
}
function fmtClock(s) {
  const x = Math.max(0, Math.round(s));
  return `${Math.floor(x / 60)}:${String(x % 60).padStart(2, '0')}`;
}

export function openTestStrip() {
  const last = loadLast();
  const state = {
    base: Number.isFinite(last.base) ? last.base : 12,
    strips: [3, 5, 7].includes(last.strips) ? last.strips : 5,
    stepStops: Number.isFinite(last.stepStops) ? last.stepStops : 1,
  };
  const body = document.createElement('div');
  body.innerHTML = `
    <div class="srow">
      <span class="srow__k">基準時間<br><span class="en" style="font-size:10px">0.5 刻み</span></span>
      <button class="cond__pm" type="button" data-base="-1" style="width:52px;height:44px" aria-label="短くする"><svg class="i" width="16" height="16"><use href="#i-minus"/></svg></button>
      <label class="cond__v" style="font-size:24px;display:flex;align-items:baseline;justify-content:center"><input class="num" id="ts-base-in" type="number" inputmode="decimal" min="1" max="120" step="0.5" aria-label="基準時間（秒）" style="width:3.6em;text-align:right;font:inherit" /><small>s</small></label>
      <button class="cond__pm" type="button" data-base="1" style="width:52px;height:44px" aria-label="長くする"><svg class="i" width="16" height="16"><use href="#i-plus"/></svg></button>
    </div>
    <div class="srow"><span class="srow__k">本数</span><div class="seg" id="ts-n">${[3, 5, 7].map((v) => `<button type="button" class="seg__opt en" data-n="${v}" style="font-size:15px">${v}</button>`).join('')}</div></div>
    <div class="srow" style="border-bottom-color:var(--color-ink)"><span class="srow__k">刻み</span><div class="seg" id="ts-step">${[[1, '1 段'], [0.5, '½ 段'], [0.333, '⅓ 段']].map(([v, t]) => `<button type="button" class="seg__opt" data-step="${v}">${t}</button>`).join('')}</div></div>
    <div style="padding:14px var(--gutter) 30px">
      <div class="ts-bars" id="ts-bars"></div>
      <div class="ts-table" id="ts-table"></div>
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px;margin-top:14px"><span class="field__label" style="margin:0;flex:none">タイマー手順</span><span class="en" id="ts-seq" style="font:500 13px/1.5 var(--font-en);text-align:right"></span></div>
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin-top:12px"><span class="field__label" style="margin:0">合計</span><span class="en" id="ts-total" style="font:400 24px/1 var(--font-en)"></span></div>
    </div>`;
  openSheet({ en: 'test strip', title: 'テストストリップ計算機', body, full: true });

  const refresh = () => {
    saveLast(state);
    const r = calculate(state.base, state.strips, state.stepStops);
    const bIn = body.querySelector('#ts-base-in');
    if (document.activeElement !== bIn) bIn.value = state.base.toFixed(1);
    body.querySelectorAll('[data-n]').forEach((b) => b.classList.toggle('is-on', Number(b.dataset.n) === state.strips));
    body.querySelectorAll('[data-step]').forEach((b) => b.classList.toggle('is-on', Number(b.dataset.step) === state.stepStops));
    const n = r.cumulative.length;
    body.querySelector('#ts-bars').innerHTML = r.cumulative.map((_, i) => `<span class="zc-${Math.round(8 - (i * 7) / Math.max(1, n - 1))}" style="animation-delay:${i * 50}ms"></span>`).join('');
    body.querySelector('#ts-table').innerHTML = `<div><span>#</span><span>累積</span><span>追加</span></div>${r.cumulative.map((c, i) => `<div><span class="sub">${i + 1}</span><span>${fmtSec(c)}</span><span>${i ? `+${fmtSec(r.deltas[i - 1])}` : fmtSec(c)}</span></div>`).join('')}`;
    body.querySelector('#ts-seq').textContent = [fmtSec(state.base), ...r.deltas.map((d) => `+${fmtSec(d)}`)].join(', ');
    body.querySelector('#ts-total').innerHTML = `${fmtSec(r.totalSec)} <span class="sub" style="font-size:13px">(${fmtClock(r.totalSec)})</span>`;
  };
  body.querySelectorAll('[data-base]').forEach((b) => holdRepeat(b, () => {
    state.base = Math.max(1, Math.min(120, Math.round((state.base + Number(b.dataset.base) * 0.5) * 2) / 2));
    refresh();
  }));
  body.querySelector('#ts-base-in').addEventListener('input', (e) => {
    const v = parseFloat(e.target.value);
    if (Number.isFinite(v) && v >= 1 && v <= 120) { state.base = v; refresh(); }
  });
  body.querySelectorAll('[data-n]').forEach((b) => b.addEventListener('click', () => { state.strips = Number(b.dataset.n); buzz(6); refresh(); }));
  body.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => { state.stepStops = Number(b.dataset.step); buzz(6); refresh(); }));
  refresh();
}
